// js/core/Game.js
// 狀態機 + 固定步長主迴圈，串接物理／渲染／輸入／HUD／結果。
// 狀態：idle（選關背景）→ draw（畫路線）→ sail（自動趕路）→ won/lost（結果）。

import { LEVELS, levelById } from '../data/levels.js';
import { freshLevel, evaluate } from './Level.js';
import { Boat } from './Boat.js';
import { Route } from './Route.js';
import { step } from './Physics.js';
import { updateDynamics } from './Dynamics.js';
import { Input } from './Input.js';
import { Renderer } from '../render/Renderer.js';
import { Hud } from '../ui/Hud.js';
import { ResultScreen } from '../ui/ResultScreen.js';
import { Storage } from './Storage.js';

const COLS = 20, ROWS = 12, CELL = 16;
const STEP = 1 / 120;     // 物理固定步長
const DEFAULT_TIME = 30;  // 預設打卡限時（秒）；關卡可以 rules.timeLimit 覆寫
const MIN_LAUNCH = 0.6;   // 最短可出發的路線長度（格）
const STALL_SPEED = 0.25; // 路線走完後視為停滯的速度門檻
const STALL_TIME = 0.6;

export class Game {
  constructor(canvas, overlay, { onExit } = {}) {
    this.canvas = canvas;
    this.overlay = overlay;
    this.onExit = onExit;

    this.renderer = new Renderer(canvas, COLS, ROWS, CELL);
    this.hud = new Hud(overlay);
    this.result = new ResultScreen(overlay);
    this.hud.onClear(() => this.restart());
    this.hud.onMenu(() => this.exit());

    this.input = new Input(canvas, {
      toWorld: (cx, cy) => this.renderer.clientToWorld(cx, cy),
      onDrawStart: () => { if (this.state === 'draw') this.route.clear(); },
      onPoint: (p) => { if (this.state === 'draw') this.route.extend(p); },
      onDrawEnd: () => { if (this.state === 'draw' && this.route.length >= MIN_LAUNCH) this.launch(); },
    });

    this.state = 'idle';
    this.def = null;
    this._lastTs = null;
    this._raf = requestAnimationFrame((t) => this._loop(t));
  }

  layout(cssW, cssH, dpr) {
    this._cssW = cssW;
    this._cssH = cssH;
    this._dpr = dpr || window.devicePixelRatio || 1;
    this._relayout();
  }

  // 量度 HUD 實際高度，預留上下帶後重新佈局——保證目標／提示／按鈕永遠唔會遮住遊戲
  _relayout() {
    if (this._cssW == null) return;
    let padTop = 0, padBottom = 0;
    const hudEl = this.hud.el;
    if (hudEl && !hudEl.classList.contains('hidden')) {
      padTop = hudEl.offsetHeight + 8;
      const hint = this.hud.hint;
      const hintH = (hint && hint.textContent) ? Math.max(hint.offsetHeight, 44) + 16 : 0;
      const actions = hudEl.querySelector('.hud-actions');
      const actH = actions ? actions.offsetHeight + 16 : 0;
      padBottom = Math.max(hintH, actH);
    }
    this.renderer.layout(this._cssW, this._cssH, this._dpr, padTop, padBottom);
  }

  start(levelId) {
    this.def = levelById(levelId) || LEVELS[0];
    this.timeLimit = this.def.rules.timeLimit ?? DEFAULT_TIME;
    this._resetLevel();
    this.result.hide();
    this.hud.show();
    this.hud.setHint(this.def.hint);
    this.hud.setHintVisible(true);
    this.hud.setGoals(this._liveGoals(), this.def.rules.maxRouteLength);
    this.input.attach();
    this.state = 'draw';
    this._relayout();
  }

  _resetLevel() {
    this.level = freshLevel(this.def);
    // 動態物件（擺動人潮／紅綠燈病菌／巡邏病菌）：複製可變狀態，每幀更新
    this.dynCurrents = (this.def.currents || []).map((c) => ({ ...c }));
    this.dynHazards = this.level.hazards;
    this.dynGates = (this.def.gates || []).map((g) => ({ ...g }));
    updateDynamics(this.dynCurrents, this.dynHazards, 0, this.dynGates);
    this.physLevel = {
      terrain: this.def.terrain,
      currents: this.dynCurrents,
      windZones: this.def.windZones,
      destinations: this.level.destinations,
      hazards: this.dynHazards,
      collectibles: this.level.collectibles,
      oilZones: this.def.oilZones || [],
      bumpers: this.def.bumpers || [],
      gates: this.dynGates,
    };
    this.boat = new Boat(this.def.start, this.def.boat);
    this.route = new Route(this.def.start, this.def.rules.maxRouteLength, this.def.terrain);
    this.elapsed = 0;
    this._acc = 0;
    this._stall = 0;
  }

  launch() {
    this.state = 'sail';
    this.input.detach();
    this.hud.setHintVisible(false);
  }

  // 「重畫」：任何時候（含航行中／結果頁）立即中止並回到畫線狀態
  restart() {
    if (!this.def || this.state === 'idle') return;
    this._resetLevel();
    this.result.hide();
    this.hud.show();
    this.hud.setHint(this.def.hint);
    this.hud.setHintVisible(true);
    this.hud.setGoals(this._liveGoals(), this.def.rules.maxRouteLength);
    this.input.attach();
    this.state = 'draw';
    this._relayout();
  }

  exit() {
    this.state = 'idle';
    this.input.detach();
    this.hud.hide();
    this.result.hide();
    if (this.onExit) this.onExit();
  }

  // 把目標對應成即時 {text, met}
  _liveGoals() {
    const d = this.def;
    const collected = this.boat ? this.boat.collected : [];
    const collisions = this.boat ? this.boat.collisions : 0;
    const delivered = this.boat ? this.boat.delivered : false;
    return d.goals.map((g) => {
      let met = false;
      switch (g.type) {
        case 'deliver': met = delivered; break;
        case 'noCollision': met = collisions === 0; break;
        case 'collect': met = collected.includes(g.target); break;
        case 'collectAll': met = collected.length === d.collectibles.length; break;
        case 'efficiency': met = this.route.length <= (g.maxLength ?? Infinity); break;
        default: met = false;
      }
      return { text: g.text, met };
    });
  }

  _loop(ts) {
    if (this._lastTs == null) this._lastTs = ts;
    let dt = (ts - this._lastTs) / 1000;
    this._lastTs = ts;
    if (dt > 0.05) dt = 0.05;

    if (this.state === 'sail') this._simulate(dt);

    if (this.def && this.state !== 'idle') {
      updateDynamics(this.dynCurrents, this.dynHazards, this.elapsed, this.dynGates);
    }
    this.renderer.render(this._buildScene(), dt);

    if (this.def && (this.state === 'draw' || this.state === 'sail')) {
      this.hud.setGoals(this._liveGoals(), this.def.rules.maxRouteLength);
      this.hud.update({
        durability: this.boat.durability,
        maxDurability: this.boat.maxDurability,
        routeUsed: this.route.length,
        routeMax: this.def.rules.maxRouteLength,
        time: Math.max(0, this.timeLimit - this.elapsed),
        timeLimit: this.timeLimit,
      });
    }

    this._raf = requestAnimationFrame((t) => this._loop(t));
  }

  _simulate(dt) {
    this.elapsed += dt;
    this._acc += dt;
    let guard = 0;
    while (this._acc >= STEP && guard < 1000) {
      const events = step(this.boat, this.route, this.physLevel, STEP);
      this._acc -= STEP;
      guard++;
      for (const e of events) {
        if (e.type === 'deliver') { this._finish(true); return; }
        if (e.type === 'fail') { this._finish(false, e.reason); return; }
      }
      if (this.state !== 'sail') return;
    }

    if (this.elapsed > this.timeLimit) { this._finish(false, 'timeout'); return; }

    // 路線走完且近乎停止 → 未抵達
    if (this.boat.wp >= this.route.points.length) {
      if (this.boat.speed < STALL_SPEED) {
        this._stall += dt;
        if (this._stall > STALL_TIME) this._finish(this.boat.delivered, this.boat.delivered ? null : 'stalled');
      } else {
        this._stall = 0;
      }
    }
  }

  _finish(delivered, reason) {
    if (this.state !== 'sail') return;
    this.state = delivered ? 'won' : 'lost';
    this.input.detach();
    this.hud.setHintVisible(false);

    const ev = evaluate(this.def, this.boat, this.route, this.elapsed);
    this.lastState = this._saveProgress(ev);

    const idx = LEVELS.findIndex((l) => l.id === this.def.id);
    const next = LEVELS[idx + 1];
    const hasNext = !!delivered && !!next;

    this.hud.hide();
    this.result.show(ev, delivered ? 'win' : 'lose', {
      hasNext,
      exportCode: Storage.exportCode(this.lastState),
      onRetry: () => this.start(this.def.id),
      onNext: () => (next ? this.start(next.id) : this.exit()),
      onMenu: () => this.exit(),
    });
  }

  _saveProgress(ev) {
    const state = Storage.load();
    const id = this.def.id;
    const rec = state.levels[id] || { stars: 0, completed: false };
    const wasCompleted = !!rec.completed;
    state.levels[id] = {
      stars: Math.max(rec.stars || 0, ev.stars),
      completed: wasCompleted || ev.delivered,
    };
    if (ev.delivered && !wasCompleted) {
      state.coffee = (state.coffee || 0) + (this.def.rewards.coffee || 0);
      state.materials = (state.materials || 0) + (this.def.rewards.materials || 0);
    }
    Storage.save(state);
    return state;
  }

  _buildScene() {
    if (!this.def || this.state === 'idle') {
      return { terrain: [], currents: [], windZones: [], hazards: [], collectibles: [], destinations: [] };
    }
    return {
      terrain: this.def.terrain,
      currents: this.dynCurrents,
      windZones: this.def.windZones,
      hazards: this.dynHazards,
      collectibles: this.level.collectibles,
      destinations: this.level.destinations,
      theme: this.def.theme,
      oilZones: this.def.oilZones,
      bumpers: this.def.bumpers,
      gates: this.dynGates,
      route: this.route,
      routeActive: this.state === 'draw',
      boat: this.boat,
    };
  }
}
